import { useState } from 'react';
import { useForms } from '@wordpuppi/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * Toolbar button that inserts a `<!-- wpp:form slug="…" -->` embed marker (AB#617).
 * Mirrors the TeraEditor dialog. `onInsert` receives the ready-made marker string;
 * the caller decides where it goes (append to content, editor command, etc.).
 *
 * ponytail: for the rich-text (Tiptap) editor the marker is appended to content and
 * ProseMirror drops HTML comments on re-serialize, so it survives insert→save but a
 * later edit can strip it — the durable path is the Edit Form "Add to your site" panel.
 */
export function InsertFormButton({
  siteSlug,
  onInsert,
  className = 'inline-flex items-center justify-center h-8 px-2 text-sm rounded-md text-muted-foreground hover:bg-background cursor-pointer',
}: {
  siteSlug: string;
  onInsert: (marker: string) => void;
  className?: string;
}) {
  const { data } = useForms(siteSlug);
  const forms = data?.data ?? [];
  const [open, setOpen] = useState(false);

  if (forms.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <span className={className} role="button" aria-label="Insert form">
          Form
        </span>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Insert Form</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {forms.map((form) => (
            <button
              key={form.id}
              type="button"
              className="w-full text-left p-3 rounded-lg border hover:bg-muted transition-colors"
              onClick={() => {
                onInsert(`<!-- wpp:form slug="${form.slug}" -->`);
                setOpen(false);
                toast.success('Form inserted');
              }}
            >
              <div className="font-medium">{form.name}</div>
              <div className="text-sm text-muted-foreground font-mono">{form.slug}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
